import Captcha from 'Components/ContractGenerator/layouts/Captcha';
import Card from 'Components/ContractGenerator/layouts/Card';
import PriceSummary from 'Components/ContractGenerator/layouts/PriceSummary';
import Section from 'Components/ContractGenerator/layouts/Section';
import Constants from 'Components/ContractGenerator/shared/constants';
import useErrorMessage from 'Components/ContractGenerator/shared/helpers/useErrorMessage';
import { primaryInput } from 'detect-it';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { appSelectors } from 'Redux/ducks/app';
import { contractGeneratorOperations, contractGeneratorSelectors } from 'Redux/ducks/contractGenerator';
import selectorStateComparison from 'Redux/utilities/selectorStateComparison';
import Alert from 'ViatoUi/Extranet/Surfaces/Alert';
import UserActionFeedback from 'ViatoUi/Shared/Feedback/UserActionFeedback';
import { FlexBox } from 'ViatoUi/Shared/Layouts/Flex';
import Spacer from 'ViatoUi/Shared/Layouts/Spacer';
import { Headline, Paragraph } from 'ViatoUi/Shared/Typo';
import Translation from 'ViatoUi/Shared/Utilities/Translation';
import Button from 'ViatoUi/Web/FormElements/Buttons/Button';
import Checkbox from 'ViatoUi/Web/FormElements/Checkbox';

import styles from './Step4.module.scss';

const Step4 = () => {
	const dispatch = useDispatch();
	const { t } = useTranslation('contractGenerator');

	const queryString = window.location.search;
	const urlParams = new URLSearchParams(queryString);
	const sessionId = urlParams.get('cgs');

	const [agbChecked, setAgbChecked] = useState(false);

	const language = useSelector(appSelectors.getLanguage);
	const sharedConstants = useSelector(contractGeneratorSelectors.getConstants);
	const dataToSubmit = useSelector(contractGeneratorSelectors.getDataToSubmit);
	const { captchaResponse } = dataToSubmit;

	const [submitCallPending, submitCallError, submitCallErrorCode, submitCallErrorMessage] = useSelector(
		contractGeneratorSelectors.getSubmitApiCall(),
		(formerState, currentState) => selectorStateComparison(formerState, currentState)
	);

	const getTooltip = () => {
		if (!agbChecked) {
			return t('pages.step4.tooltips.gtc');
		}

		if (captchaResponse === '') {
			return t('app.captchaRequired');
		}

		return null;
	};

	const errorMessage = useErrorMessage(submitCallErrorCode);

	return (
		<>
			<Section firstElementOfStep>
				<div className={styles.content}>
					<Headline type='h1'>{t('pages.step4.headline')}</Headline>

					<Spacer height='xl' />
					<Card>
						<PriceSummary
							headline={
								<>
									<Headline type='h4'>{t('pages.step4.totalCosts')}:</Headline>
									<Spacer height='xs' />
								</>
							}
							showFinalPrice
						/>
						<Spacer height='xl' />
						<Checkbox
							checked={agbChecked}
							label={
								<Paragraph font='normal'>
									<Translation
										components={[
											{
												type: 'anchor',
												props: { href: sharedConstants.COMPANY.GTC_LINK, target: '_blank' }
											},
											{
												type: 'anchor',
												props: { href: sharedConstants.COMPANY.PRIVACY_LINK, target: '_blank' }
											}
										]}
										translationKey='pages.step4.gtcAndPrivacyPolicy'
										translationNamespace='contractGenerator'
									/>
								</Paragraph>
							}
							onToggle={() => setAgbChecked(!agbChecked)}
						/>

						<Spacer height='l' />
						<Captcha />
						<Spacer height='xl' />
						{primaryInput === 'touch' && getTooltip() !== null && (
							<>
								<Alert type='warning'>{getTooltip()}</Alert>
								<Spacer height='l' />
							</>
						)}
						<FlexBox className={styles.buttonWrap}>
							<Button
								className={styles.submit}
								disabled={!agbChecked || captchaResponse === ''}
								tooltip={getTooltip()}
								type='secondary'
								onClick={() =>
									dispatch(
										contractGeneratorOperations.submit({ ...dataToSubmit, language, session: sessionId || 'noSession' })
									)
								}
							>
								{t('app.userActions.submit')}
							</Button>
							<Spacer width='s' />
							<Button type='ghost' onClick={() => dispatch(contractGeneratorOperations.setActiveStep(Constants.STEPS.STEP3))}>
								{t('app.userActions.back')}
							</Button>
						</FlexBox>

						<Spacer height='l' />

						<Paragraph color='silver' font='normal'>
							{t('app.taxInfo')}
						</Paragraph>
					</Card>
				</div>
			</Section>
			<UserActionFeedback
				error={!!submitCallError}
				errorCode={submitCallErrorCode}
				errorMessage={submitCallErrorMessage}
				pending={!!submitCallPending}
				type='modal'
				userMessage={errorMessage}
				isSilent
				showContractLoader
				onSuccess={() => dispatch(contractGeneratorOperations.setActiveStep(Constants.STEPS.STEP5))}
			/>
		</>
	);
};

export default Step4;
